import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Link, Stack, Typography } from '@mui/material';

import { INTAKE_SERVICE_OPTIONS, type SelectableIntakeService } from './IntakeServiceSelectionCard';

const SUMMARY_DESCRIPTIONS: Record<SelectableIntakeService, string> = {
  jotbill: 'Billing set up for your practice.',
  jotassure: 'Certified claims for managed billing, on top of JotBill.',
  jotcred: 'Credentialing prepared for your providers.',
};

/** The practice's saved services as a statement, not a control; Agreements & setup owns the choice. */
export function IntakeServicesSummary({
  services,
  canManage,
  signedLocked = false,
  onReviewAgreements,
}: {
  services: SelectableIntakeService[];
  /** A practice admin gets the way to Agreements & setup; a clinician is told who to ask. */
  canManage: boolean;
  signedLocked?: boolean;
  onReviewAgreements?: () => void;
}) {
  const agreementsAndSetup =
    canManage && onReviewAgreements ? (
      <Link
        component="button"
        type="button"
        onClick={onReviewAgreements}
        sx={{ fontSize: 'inherit', fontWeight: 600, verticalAlign: 'baseline' }}
      >
        Agreements &amp; setup
      </Link>
    ) : (
      <Box component="span" sx={{ fontWeight: 600 }}>
        Agreements &amp; setup
      </Box>
    );
  return (
    <Stack spacing={2}>
      <Box>
        <Typography component="h2" sx={{ fontSize: '16px', fontWeight: 650, lineHeight: 1.5 }}>
          Services you&apos;re setting up
        </Typography>
        {services.length > 0 && (
          <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
            These are the services your practice signed up for. They decide what information we ask
            for below.
          </Typography>
        )}
      </Box>
      {services.length === 0 ? (
        <Alert severity="warning">
          {canManage ? (
            <>
              Your practice hasn&apos;t chosen its services yet. Choose them on {agreementsAndSetup}{' '}
              to continue. We only ask for what those services need.
            </>
          ) : (
            <>
              Your practice admin hasn&apos;t chosen your practice&apos;s services yet. Ask them to
              choose them on {agreementsAndSetup} so you can continue. We only ask for what those
              services need.
            </>
          )}
        </Alert>
      ) : (
        <>
          <Box
            component="ul"
            aria-label="Services you're setting up"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            {INTAKE_SERVICE_OPTIONS.filter(option => services.includes(option.id)).map(
              (option, index) => (
                <Box
                  component="li"
                  key={option.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '130px minmax(0, 1fr)' },
                    columnGap: 2,
                    px: 2,
                    py: 1.25,
                    borderTop: index === 0 ? 0 : 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.5 }}>
                    {option.label}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.6 }}>
                    {SUMMARY_DESCRIPTIONS[option.id]}
                  </Typography>
                </Box>
              ),
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <InfoOutlinedIcon
              aria-hidden="true"
              sx={{ fontSize: '18px', mt: '2px', color: 'text.secondary' }}
            />
            <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
              {signedLocked ? (
                'Your practice has signed its agreements, so these services can no longer be changed. Contact JotPsych support to make a change.'
              ) : (
                <>
                  To add or remove a service, a practice admin updates it on {agreementsAndSetup}.
                </>
              )}
            </Typography>
          </Box>
        </>
      )}
    </Stack>
  );
}
